import { SkeletonLines } from '@upright/ui/common/generic/components/skeleton/SkeletonLines';
import clsx from 'clsx';
import { cloneElement, type ReactElement, type ReactNode, type Ref, useContext, Children } from 'react';
import { SkeletonContext } from './context';
import { useLoadingAnimationCallbackRef, useMergedRef, inertValue } from './utils';
import { mergeProps } from 'react-aria';
import styles from './Skeleton.module.css';

export type Props = {
  lines?: number;
  minBlockSize?: string;
  minInlineSize?: string;
  children: ReactElement<{
    children: ReactNode;
    className?: string;
    inert?: boolean | 'true';
    ref?: Ref<HTMLElement>;
  }> & {
    ref?: Ref<HTMLElement>;
  };
};

export function Skeleton({ minBlockSize, minInlineSize, lines, children }: Props) {
  const isLoading = useContext(SkeletonContext);
  const animationRef = useLoadingAnimationCallbackRef();
  const child = Children.only(children);
  const attachedRef = child.props.ref ?? child.ref;
  const ref = useMergedRef(attachedRef, animationRef);

  if (isLoading == null) {
    return child;
  }

  const grandchildren = child.props.children;

  return (
    <SkeletonContext.Provider value={null}>
      {isLoading
        ? cloneElement(
            child,
            mergeProps(child.props, {
              ref,
              inert: inertValue(true),
              style: { minInlineSize, minBlockSize },
              className: lines ? undefined : clsx(styles.root, styles.animation),
            }),
            lines ? (
              <SkeletonLines lines={lines} />
            ) : grandchildren ? (
              typeof grandchildren === 'string' && !grandchildren.trim() ? (
                <>&nbsp;</>
              ) : (
                grandchildren
              )
            ) : (
              <>&nbsp;</>
            ),
          )
        : child}
    </SkeletonContext.Provider>
  );
}
